/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { User, Mail, Phone, Linkedin, ArrowLeft, Award, Users, Crown, Briefcase } from 'lucide-react';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  founder: { label: 'Our Founder', icon: Crown, color: '#E63946' },
  executive: { label: 'Executive Board', icon: Award, color: '#002249' },
  director: { label: 'Board of Directors', icon: Users, color: '#457B9D' },
  committee: { label: 'Committee Directors', icon: Briefcase, color: '#1D3557' },
};

const LeaderProfilePage = () => {
  const { leaderId } = useParams();
  const [leader, setLeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedLeaders, setRelatedLeaders] = useState([]);

  useEffect(() => {
    fetchLeader();
  }, [leaderId]);

  const fetchLeader = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leadership/${leaderId}`);
      setLeader(res.data);
      // Fetch others in same category
      const allRes = await axios.get(`${API}/leadership?category=${res.data.category}`);
      setRelatedLeaders(allRes.data.filter(l => l.id !== leaderId).slice(0, 4));
    } catch (err) {
      // error handled silently
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 sm:pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#002249] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!leader) {
    return (
      <div className="min-h-screen pt-16 sm:pt-20 flex flex-col items-center justify-center text-center px-4">
        <User className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-[#002249] mb-2">Leader Not Found</h2>
        <p className="text-slate-500 mb-6">This leader profile may have been removed or is not currently active.</p>
        <Link to="/leadership" className="text-[#E63946] hover:underline font-medium flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />Back to Leadership
        </Link>
      </div>
    );
  }

  const catMeta = CATEGORY_META[leader.category] || CATEGORY_META.director;
  const CatIcon = catMeta.icon;
  const imgSrc = leader.image_url
    ? (resolveImg(leader.image_url))
    : null;
  const bannerSrc = leader.banner_url
    ? (resolveImg(leader.banner_url))
    : null;

  return (
    <div className="min-h-screen pt-16 sm:pt-20" data-testid="leader-profile-page">
      {/* Hero Band */}
      <section className="relative bg-[#002249] overflow-hidden">
        {bannerSrc ? (
          <div className="absolute inset-0">
            <Img variant="generic" src={bannerSrc} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#002249]/70" />
          </div>
        ) : (
          <div className="absolute inset-0 opacity-[0.04]">
            <svg width="100%" height="100%"><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/></pattern><rect width="100%" height="100%" fill="url(#grid)"/></svg>
          </div>
        )}
        <div className="container-custom relative z-10 py-8 sm:py-14">
          <Link to="/leadership" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 sm:mb-8 transition-colors" data-testid="back-to-leadership">
            <ArrowLeft className="w-4 h-4" />Back to Leadership
          </Link>
          <div className="flex flex-col md:flex-row gap-6 sm:gap-8 items-start">
            {/* Photo */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
              className="w-32 h-32 sm:w-56 sm:h-56 rounded-sm overflow-hidden flex-shrink-0 border-2 border-white/10 shadow-2xl mx-auto md:mx-0">
              {imgSrc ? (
                <Img variant="leader" src={imgSrc} alt={leader.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#457B9D] to-[#002249] flex items-center justify-center">
                  <User className="w-20 h-20 text-white/20" />
                </div>
              )}
            </motion.div>

            {/* Info */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-2 mb-3 justify-center md:justify-start">
                <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${catMeta.color}40` }}>
                  <CatIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-bold tracking-wider uppercase" style={{ color: catMeta.color === '#002249' ? '#E63946' : catMeta.color }}>
                  {catMeta.label}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2" data-testid="leader-profile-name">{leader.name}</h1>
              <p className="text-lg sm:text-xl text-[#E63946] font-medium mb-4" data-testid="leader-profile-title">{leader.title}</p>
              {leader.bio && (
                <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-2xl">{leader.bio}</p>
              )}

              {/* Contact Links */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-5 sm:mt-6 justify-center md:justify-start">
                {leader.email && (
                  <a href={`mailto:${leader.email}`} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-sm text-white text-sm transition-colors" data-testid="leader-email-link">
                    <Mail className="w-4 h-4" />{leader.email}
                  </a>
                )}
                {leader.phone && (
                  <a href={`tel:${leader.phone}`} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-sm text-white text-sm transition-colors" data-testid="leader-phone-link">
                    <Phone className="w-4 h-4" />{leader.phone}
                  </a>
                )}
                {leader.linkedin && (
                  <a href={leader.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-sm text-white text-sm transition-colors" data-testid="leader-linkedin-link">
                    <Linkedin className="w-4 h-4" />LinkedIn
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Full Bio */}
      {(leader.full_bio || leader.bio) && (
        <section className="section-padding bg-white">
          <div className="container-custom max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-xl sm:text-2xl font-bold text-[#002249] mb-6">About {leader.name.split(' ')[0]}</h2>
              <div className="prose prose-slate max-w-none text-slate-600 text-sm sm:text-base leading-relaxed whitespace-pre-line" data-testid="leader-full-bio">
                {leader.full_bio || leader.bio}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Related Leaders */}
      {relatedLeaders.length > 0 && (
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
              <span className="text-xs font-bold text-[#E63946] tracking-wider uppercase mb-2 block">{catMeta.label}</span>
              <h2 className="text-xl sm:text-2xl font-bold text-[#002249]">Other Leaders</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {relatedLeaders.map(rl => {
                const rlImg = rl.image_url
                  ? (resolveImg(rl.image_url))
                  : null;
                return (
                  <Link key={rl.id} to={`/leadership/${rl.id}`} className="group" data-testid={`related-leader-${rl.id}`}>
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      className="bg-white rounded-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
                      <div className="aspect-[4/3] bg-gradient-to-br from-[#002249] to-[#457B9D] relative">
                        {rlImg ? (
                          <Img variant="generic" src={rlImg} alt={rl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center"><User className="w-12 h-12 text-white/20" /></div>
                        )}
                      </div>
                      <div className="p-4 text-center">
                        <h3 className="font-semibold text-[#002249] text-sm group-hover:text-[#E63946] transition-colors">{rl.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{rl.title}</p>
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LeaderProfilePage;
